import axiosInstance from "./axiosInstance";

const BULK_LIST_URL = "/merchant/disbursements/bulk";
const BULK_TEMPLATE_URL = "/merchant/disbursements/bulk/template";
const BULK_SUMMATION_URL = "/merchant/disbursements/bulk/summation";
const BULK_RECIPIENTS_URL = (bulkId) =>
  `/merchant/disbursements/bulk/${bulkId}/recipients`;
const BULK_CHARGES_URL = "/merchant/disbursements/bulk/charges";
const BULK_LIMITS_URL = "/merchant/disbursements/limits";
const BULK_HAKIKISHA_URL = "/merchant/disbursements/hakikisha";
const BULK_EVENTS_URL = (id) => `/merchant/disbursements/bulk/${id}/events`;
const BULK_UPLOAD_URL = "/merchant/disbursements/bulk/upload";
const BULK_RECIPIENT_URL = (disbursementId, recipientId) =>
  `/merchant/disbursements/bulk/${disbursementId}/recipients/${recipientId}`;

const BULK_APPROVE_URL = (disbursementId) =>
  `/merchant/disbursements/bulk/${disbursementId}/approve`;

const BULK_STATUS_FILTER_URL = "/merchant/disbursements/bulk/filter/status";
const BULK_RECIPIENT_STATUS_FILTER_URL =
  "/merchant/disbursements/bulk/filter/recipients/status";

const MANAGEMENT_BULK_BASE_URL = "/management/disbursements/bulk";
const MANAGEMENT_BULK_SUMMATION_URL = `${MANAGEMENT_BULK_BASE_URL}/summation`;
const MANAGEMENT_BULK_RECIPIENTS_URL = (id) =>
  `${MANAGEMENT_BULK_BASE_URL}/${id}/recipients`;
const MANAGEMENT_BULK_EVENTS_URL = (id) =>
  `${MANAGEMENT_BULK_BASE_URL}/${id}/events`;

const bulkUploadsService = {
  getBulkSummation: async () => {
    const response = await axiosInstance.get(BULK_SUMMATION_URL);
    return response.data;
  },

  getBatchHistory: async ({
    searchQuery,
    status,
    startDate,
    endDate,
    page = 0,
    size = 10,
  }) => {
    const params = { page, size };
    if (searchQuery) params.searchQuery = searchQuery;
    if (status) params.status = status;
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    const response = await axiosInstance.get(BULK_LIST_URL, { params });
    return response.data;
  },

  getBatchRecipients: async (
    bulkId,
    { searchQuery, status, startDate, endDate, page = 0, size = 10 } = {},
  ) => {
    const params = { page, size };
    if (searchQuery) params.searchQuery = searchQuery;
    if (status) params.status = status;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await axiosInstance.get(BULK_RECIPIENTS_URL(bulkId), {
      params,
    });
    return response.data;
  },

  getBatchDetail: async (id) => {
    const response = await axiosInstance.get(`${BULK_LIST_URL}/${id}`);
    return response.data;
  },

  createBulkDisbursement: async (payload) => {
    const response = await axiosInstance.post(BULK_LIST_URL, payload);
    return response.data;
  },

  getDisbursementCharges: async (payload) => {
    const response = await axiosInstance.post(BULK_CHARGES_URL, payload);
    return response.data;
  },

  getDisbursementLimits: async (paymentChannel) => {
    const response = await axiosInstance.post(BULK_LIMITS_URL, {
      paymentChannel,
    });
    return response.data;
  },

  hakikisha: async (payload) => {
    const response = await axiosInstance.post(BULK_HAKIKISHA_URL, payload);
    return response.data;
  },

  getBatchEvents: async (id) => {
    const response = await axiosInstance.get(BULK_EVENTS_URL(id));
    return response.data;
  },

  downloadTemplate: async () => {
    const response = await axiosInstance.get(BULK_TEMPLATE_URL, {
      responseType: "blob",
    });
    return {
      blob: response.data,
      contentDisposition: response.headers["content-disposition"] ?? "",
    };
  },

  exportBatchHistory: async (_filters) => {
    throw new Error("Export not yet available.");
  },

  exportBatchRecipients: async (_bulkId, _filters) => {
    throw new Error("Export not yet available.");
  },

  uploadBulkFile: async (formData) => {
    const response = await axiosInstance.post(BULK_UPLOAD_URL, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  updateBulkRecipient: async (disbursementId, recipientId, payload) => {
    const response = await axiosInstance.put(
      BULK_RECIPIENT_URL(disbursementId, recipientId),
      payload,
    );
    return response.data;
  },

  approveBulkBatch: async (disbursementId) => {
    const response = await axiosInstance.patch(
      BULK_APPROVE_URL(disbursementId),
    );
    return response.data;
  },

  getBulkDisbursementStatusFilters: async () => {
    const response = await axiosInstance.get(BULK_STATUS_FILTER_URL);
    return response.data;
  },

  getBulkDisbursementRecipientStatusFilters: async () => {
    const response = await axiosInstance.get(BULK_RECIPIENT_STATUS_FILTER_URL);
    return response.data;
  },

  getManagementBulkSummation: async () => {
    const response = await axiosInstance.get(MANAGEMENT_BULK_SUMMATION_URL);
    return response.data;
  },

  getManagementBatchHistory: async ({
    searchQuery,
    status,
    startDate,
    endDate,
    merchantId,
    page = 0,
    size = 10,
  }) => {
    const params = { page, size };
    if (searchQuery) params.searchQuery = searchQuery;
    if (status) params.status = status;
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    if (merchantId) params.merchantId = merchantId;
    const response = await axiosInstance.get(MANAGEMENT_BULK_BASE_URL, {
      params,
    });
    return response.data;
  },

  getManagementBatchDetail: async (id) => {
    const response = await axiosInstance.get(
      `${MANAGEMENT_BULK_BASE_URL}/${id}`,
    );
    return response.data;
  },

  getManagementBatchRecipients: async (
    id,
    { searchQuery, status, startDate, endDate, page = 0, size = 10 } = {},
  ) => {
    const params = { page, size };
    if (searchQuery) params.searchQuery = searchQuery;
    if (status) params.status = status;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await axiosInstance.get(
      MANAGEMENT_BULK_RECIPIENTS_URL(id),
      { params },
    );
    return response.data;
  },

  getManagementBatchEvents: async (id) => {
    const response = await axiosInstance.get(MANAGEMENT_BULK_EVENTS_URL(id));
    return response.data;
  },
};

export default bulkUploadsService;
